// src/components/SubscriptionBanner.js
//
// Escalating subscription banner shown inside the app shell (rendered by
// AppLayout for non-blocked users). Suppressed when active or when a trial has no
// trialEnd (grandfathered company). The Renew CTA is superuser-only: the
// Subscriptions page is guarded by set:bill:edit, so a non-superuser who clicked
// it would silently bounce to the dashboard. See PRD/PRD_Billing.md.
import React, { useLayoutEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import { PHASE, TRIAL_WARNING_DAYS } from "../config/subscription";

const TONES = {
  info: { bg: "var(--ui-primary-tint)", border: "var(--ui-primary)", text: "var(--ui-primary-hover)" },
  warn: { bg: "var(--ui-warning-tint)", border: "var(--ui-warning)", text: "var(--ui-warning-text)" },
  error: { bg: "var(--ui-danger-tint)", border: "var(--ui-danger)", text: "var(--ui-danger-text)" },
};

const plural = (n) => (n === 1 ? "" : "s");

const SubscriptionBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, canManageBilling } = useAuth();
  const { phase, daysLeft } = useSubscription();

  // Decide whether to render and what to show — keep ALL hooks above any early
  // return so the rules-of-hooks hold.
  let tone = null;
  let message = null;
  if (phase === PHASE.TRIAL && daysLeft != null) {
    tone = daysLeft <= TRIAL_WARNING_DAYS ? "warn" : "info";
    message = `${daysLeft} day${plural(daysLeft)} left in your trial.`;
  } else if (phase === PHASE.GRACE) {
    tone = "warn";
    const within = daysLeft != null ? ` within ${daysLeft} day${plural(daysLeft)}` : "";
    message = `Payment failed — renew${within} or your team will lose access.`;
  } else if (phase === PHASE.EXPIRED) {
    tone = "error";
    message = "Subscription expired — renew to restore your team's access.";
  }
  const shouldRender = message !== null;

  // Publish the banner's actual rendered height as a CSS var while mounted, so
  // viewport-fixed layouts (Settings sub-sidebar) can offset themselves exactly.
  // Measure rather than hard-code: padding + line-height + border varies with
  // font and zoom, and a few pixels off clips the banner's bottom border.
  const bannerRef = useRef(null);
  useLayoutEffect(() => {
    if (!shouldRender || !bannerRef.current) return undefined;
    const root = document.documentElement;
    const el = bannerRef.current;
    const apply = () => {
      // Use getBoundingClientRect for subpixel precision — offsetHeight rounds
      // to int, which can leave the sub-sidebar a fractional pixel too high and
      // shave the banner's 2px bottom border.
      const h = el.getBoundingClientRect().height;
      root.style.setProperty("--sahod-banner-height", `${h}px`);
    };
    apply();
    // Re-measure if the banner reflows (text wrap on narrow viewports etc.).
    // ResizeObserver may not exist in test/jsdom — degrade to a one-shot read.
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(apply);
      ro.observe(el);
    }
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty("--sahod-banner-height");
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const palette = TONES[tone];
  const needsAction = phase === PHASE.GRACE || phase === PHASE.EXPIRED;
  // The Subscribe/Renew control already lives on the Subscriptions page itself, so
  // the banner's CTA would be a no-op there — hide it when we're already on it.
  const onSubscriptionsPage = location.pathname.endsWith("/settings/subscriptions");
  const goRenew = () =>
    navigate(`/company/${company?.slug}/settings/subscriptions`);

  return (
    <div
      ref={bannerRef}
      role="status"
      style={{
        background: palette.bg,
        borderBottom: `2px solid ${palette.border}`,
        color: palette.text,
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        fontSize: "0.875rem",
        fontWeight: 500,
      }}
    >
      <span>{message}</span>

      {canManageBilling ? (
        onSubscriptionsPage ? null : (
          <button
            onClick={goRenew}
            style={{
              background: palette.border,
              color: "var(--ui-surface)",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {needsAction ? "Renew" : "Manage subscription"}
          </button>
        )
      ) : (
        needsAction && (
          <span style={{ opacity: 0.85, whiteSpace: "nowrap" }}>
            Contact your account owner / a superuser to renew.
          </span>
        )
      )}
    </div>
  );
};

export default SubscriptionBanner;
